/**
 * The Forecast tab's Filters drawer.
 *
 * CR-6448: the supplier view gets the collapsible accordion design with a
 * header bar (matching SO Headers); the customer view keeps the flat layout
 * with a Clear all / Apply footer. Both render the same filter sections.
 */
import { ForecastFilterOptions } from '../../../../lib/api';
import { Button } from '../../../ui/button';
import { ChevronDown, Search, X } from 'lucide-react';
import { ForecastFilterSections } from './ForecastFilterSections';
import { ForecastCascadeFilterOptions } from './useForecastFilterCascades';
import { ForecastFilters } from './forecastGridTypes';

interface ForecastFiltersPanelProps {
  filters: ForecastFilters;
  onFilterChange: (key: string, value: string) => void;
  activeFilterCount: number;
  onApply: () => void;
  onClear: () => void;
  onHide: () => void;
  filterOptions: ForecastFilterOptions | null;
  cascadeFilterOptions: ForecastCascadeFilterOptions | null;
  isCustomer: boolean;
  isSupplier: boolean;
  isScheduleTenant: boolean;
  supplierSiteName?: string;
}

export function ForecastFiltersPanel({
  filters, onFilterChange, activeFilterCount, onApply, onClear, onHide,
  filterOptions, cascadeFilterOptions, isCustomer, isSupplier, isScheduleTenant,
  supplierSiteName,
}: ForecastFiltersPanelProps) {
  const sections = (
    <ForecastFilterSections
      filters={filters as unknown as Record<string, string>}
      onFilterChange={onFilterChange}
      filterOptions={filterOptions}
      isCustomer={isCustomer}
      isSupplier={isSupplier}
      isScheduleTenant={isScheduleTenant}
      supplierSiteName={supplierSiteName}
      divisionOptions={cascadeFilterOptions?.divisions.map((d) => ({ value: String(d.id), label: d.name })) || []}
      siteCountries={cascadeFilterOptions?.siteCountries || []}
      supplierCountries={cascadeFilterOptions?.supplierCountries || []}
      siteOptions={cascadeFilterOptions?.sites.map((s) => ({ value: String(s.id), label: s.name })) || []}
      rawCascadeOptions={cascadeFilterOptions}
    />
  );

  if (isSupplier) {
    return (
      <div className="border rounded-lg bg-card mb-4">
        <div className="flex items-center justify-between px-4 py-3 border-b bg-muted/30">
          <h3 className="text-sm font-semibold text-foreground">Forecast Filters</h3>
          <div className="flex items-center gap-2">
            <button
              onClick={onClear}
              className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"
            >
              <X className="h-3.5 w-3.5" />
              Clear all
            </button>
            <span className="text-muted-foreground">·</span>
            <button
              onClick={onHide}
              className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"
            >
              <ChevronDown className="h-3.5 w-3.5 rotate-180" />
              Hide filters
            </button>
            <button
              onClick={onApply}
              className="ml-2 px-3 py-1.5 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 flex items-center gap-1.5"
            >
              <Search className="h-3.5 w-3.5" />
              Apply Filters
            </button>
          </div>
        </div>
        {sections}
      </div>
    );
  }

  return (
    <div className="bg-slate-50 rounded-lg p-4 mb-4 border">
      <div className="flex items-center gap-3 mb-4">
        <h3 className="text-sm font-semibold">Filter forecast</h3>
        <span
          className={`px-2 py-0.5 rounded-full text-xs font-medium ${
            activeFilterCount > 0 ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
          }`}
        >
          {activeFilterCount > 0
            ? `${activeFilterCount} ${activeFilterCount === 1 ? 'filter' : 'filters'} active`
            : 'No active filters'}
        </span>
      </div>
      {sections}
      <div className="flex items-center justify-between gap-3 mt-5 pt-4 border-t">
        <Button variant="outline" onClick={onClear} size="sm">
          <X className="h-4 w-4 mr-2" />
          Clear all
        </Button>
        <Button onClick={onApply} size="sm">
          <Search className="h-4 w-4 mr-2" />
          Apply filters
        </Button>
      </div>
    </div>
  );
}
